import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DoCheck,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Entity, Question, QuestionResponse, Section } from '../../../interfaces/questions.interface';

import { Subscription } from 'rxjs/internal/Subscription';
import { QuestionTemplateDirective } from '../directives/question-template.directive';
import { CstQuestionnaireService, EntityType } from '../../../services/cst-questionnaire.service';
import { IIndexable } from '../../../../mdr-dashboards/interfaces/mdr-dashboards-widgets.interface';
import { CommonQuestionnaireService } from '../../../services/common-questionnaire.service';
import { QuestionTypesEnum } from '../../../enums/question-types.enum';
import { CifcReview } from '../../../interfaces/cifc.interface';
import * as uuid from 'uuid';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DescriptionDialogComponent } from '../description-dialog/description-dialog.component';
import { CustomerQuestionnaireService } from '../../../services/customer-questionnaire.service';
import * as _ from 'lodash';

@Component({
    selector: 'app-slideshow',
    templateUrl: './slideshow.component.html',
    styleUrls: ['./slideshow.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SlideshowComponent implements OnInit, OnDestroy, DoCheck {
    @ViewChild(QuestionTemplateDirective)
    questionTemplateDir: QuestionTemplateDirective;
    @Input() reviewState = true;
    @Input() scrollToQuestion: QuestionResponse;

    public oldScrollToQuestion: QuestionResponse;
    public currentComponent: ComponentRef<any>;
    public question: Question;
    public sections: Section[];
    public numberOfQuestions: number;
    public entity: Entity;
    public reviewPanelOpen: boolean[];
    public QuestionTypesEnum = QuestionTypesEnum;
    public initialAnswer: any;
    public reviewsArray: CifcReview[] = [];
    public subscription: Subscription;
    public cancelReviewSubscription: Subscription;
    public currentReason: string;
    /** Reviewer options and variables */
    public reviewerOptions = {
        amend: $localize`Ask customer to amend answer`,
        modify: $localize`Modify answer`,
    };

    public reviewerBannerMessage = {
        modify: $localize`CIFC has modified your answer to this question. Please review and accept the changes.`,
        amend: $localize`CIFC requests amending your answer to this question.`,
    };

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cdr: ChangeDetectorRef,
        public dialog: MatDialog,
    ) {}

    public ngOnInit(): void {
        this.createSubscriptions();
        this.commonQuestionnaireService.displayOkCifc = [];
        if (!this.commonQuestionnaireService.createMode) {
            this.subscription = this.customerQuestionnaireService.getQuestionObs().subscribe((question: Question | null) => {
                if (question) {
                    this.entity = { entity: question };
                    this.commonQuestionnaireService.currentEntity = this.entity;
                    this.cdr.detectChanges();
                }
            });
        }

        this.cancelReviewSubscription = this.commonQuestionnaireService.cancelReviewObs().subscribe((question: Question) => {
            const reviewIndex = this.commonQuestionnaireService.reviewQuestions.findIndex((x) => x.id === question.id);
            if (reviewIndex > -1) {
                let temp = _.cloneDeep(this.commonQuestionnaireService.reviewQuestions);

                (temp[reviewIndex].cifc_review as any) = _.cloneDeep(
                    this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === question.id)?.cifc_review,
                );
                this.commonQuestionnaireService.reviewQuestions[reviewIndex] = _.cloneDeep(temp[reviewIndex]);
                this.commonQuestionnaireService.reviewQuestionsSubject.next([...this.commonQuestionnaireService.reviewQuestions]);
                for (let s of this.customerQuestionnaireService.sections) {
                    const questionIndex = s.questions.findIndex((x) => x.id === question.id);
                    if (s.questions[questionIndex]) {
                        // @ts-ignore
                        s.questions[questionIndex].cifc_review = _.cloneDeep(
                            this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === question.id)?.cifc_review,
                        );
                        break;
                    }
                }
                this.cdr.detectChanges();
            }
        });

        if (!this.commonQuestionnaireService.createMode || this.commonQuestionnaireService.reviewMode) {
            this.customerQuestionnaireService.questionnaireLoadedSubject.subscribe((loaded: boolean) => {
                if (loaded) {
                    this.commonQuestionnaireService.reviewQuestions = this.customerQuestionnaireService.getAnsweredQuestionsForReview();
                    this.commonQuestionnaireService.reviewQuestionsSubject.next([
                        ...this.customerQuestionnaireService.getAnsweredQuestionsForReview(),
                    ]);
                    this.commonQuestionnaireService.initialReviewQuestions = _.cloneDeep(this.commonQuestionnaireService.reviewQuestions);
                    this.sections = this.customerQuestionnaireService.getSections();
                    this.numberOfQuestions = this.customerQuestionnaireService.calculateTotalNumberOfQuestions();
                    this.reviewPanelOpen = new Array(this.numberOfQuestions).fill(false);

                    this.commonQuestionnaireService.reviewQuestions.forEach((question) => {
                        this.reviewsArray.push(question.cifc_review ?? null);
                    });
                }
            });
        }

        if (this.commonQuestionnaireService.createMode) {
            this.cstQuestionnaireService.getCreatedTypeObs().subscribe((type: EntityType) => {
                this.entity = { entity: {} as Question } as Entity;
                (this.entity.entity as Question).type_id = type.name as QuestionTypesEnum;
                this.cdr.detectChanges();
            });

            this.subscription = this.cstQuestionnaireService.getEntityObs().subscribe((entity: Entity) => {
                this.entity = entity;
                this.cdr.detectChanges();
            });
        }
    }

    public createSubscriptions() {
        this.subscription = this.customerQuestionnaireService.getDisplayVersionObs().subscribe((version: string) => {
            this.commonQuestionnaireService.reviewQuestions =
                version === 'full'
                    ? this.customerQuestionnaireService.getAnsweredQuestionsForReview()
                    : this.customerQuestionnaireService.getMandatoryQuestionsForReview();

            this.commonQuestionnaireService.reviewQuestionsSubject.next([...this.commonQuestionnaireService.reviewQuestions]);
            this.commonQuestionnaireService.initialReviewQuestions = _.cloneDeep(this.commonQuestionnaireService.reviewQuestions);

            // if (version === 'condensed' && !(this.entity?.entity as Question).required) {
            //     this.customerQuestionnaireService.nextQuestion();
            // }

            if (version === 'condensed') {
                this.customerQuestionnaireService.setQuestion(
                    this.customerQuestionnaireService.processedCondensedSections.find((x) => !x.body.answered) ||
                        this.customerQuestionnaireService.processedCondensedSections[0],
                );
            }

            if (version === 'full') {
                this.customerQuestionnaireService.setQuestion(
                    this.customerQuestionnaireService.processedFullSections.find((x) => !x.body.answered) ||
                        this.customerQuestionnaireService.processedFullSections[0],
                );
            }

            this.cdr.detectChanges();
        });

        this.subscription = this.cstQuestionnaireService.getQuestionObs().subscribe((q) => {
            this.entity = { entity: q };
            this.cdr.detectChanges();
        });

        this.subscription = this.cstQuestionnaireService.onDeletedQuestion().subscribe((value) => {
            this.cdr.detectChanges();
        });
    }

    ngDoCheck(): void {
        if (this.commonQuestionnaireService.reviewMode && this.oldScrollToQuestion?.id !== this.scrollToQuestion?.id) {
            this.oldScrollToQuestion = this.scrollToQuestion;
            this.scrollToQuestionFn(this.scrollToQuestion);
        }

        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            this.entity = this.commonQuestionnaireService.currentEntity;
        }
    }

    ngOnDestroy(): void {
        if (this.currentComponent) {
            this.currentComponent.changeDetectorRef.detach();
        }
        this.subscription?.unsubscribe();
    }

    public showDescription() {
        this.dialog.open(DescriptionDialogComponent, {
            data: {
                title: this.cstQuestionnaireService.currentSection.section,
                description: this.cstQuestionnaireService.currentSection.description,
            },
            panelClass: 'simple-dialog',
            width: '45vw',
            backdropClass: 'description_overlay',
        } as MatDialogConfig);
    }

    public toggleReviewPanel(value: boolean, index: number) {
        this.reviewPanelOpen[index] = value;
    }

    public checkAvailability(direction: 'next' | 'previous') {
        switch (direction) {
            case 'next':
                return this.commonQuestionnaireService.createMode
                    ? this.cstQuestionnaireService.checkNextAvailability()
                    : this.customerQuestionnaireService.checkNextAvailability();
            case 'previous':
                return this.commonQuestionnaireService.createMode
                    ? this.cstQuestionnaireService.checkPreviousAvailability()
                    : this.customerQuestionnaireService.checkPreviousAvailability();
        }
    }

    public checkDirection(direction: 'next' | 'previous') {
        switch (direction) {
            case 'next':
                return this.commonQuestionnaireService.createMode
                    ? this.cstQuestionnaireService.nextEntity()
                    : this.customerQuestionnaireService.nextQuestion();
            case 'previous':
                return this.commonQuestionnaireService.createMode
                    ? this.cstQuestionnaireService.previousEntity()
                    : this.customerQuestionnaireService.previousQuestion();
        }
    }

    public parseEntityAsQuestion(entity: Question | Section | undefined) {
        return entity as Question;
    }

    public getReviewBannerMessage(type: string): string {
        return (this.reviewerBannerMessage as IIndexable)[type];
    }

    public initializeReview(question: Question): CifcReview {
        let review = this.commonQuestionnaireService.getReviewFromMapByQuestionId(question.id) || ({} as CifcReview);

        if (!review) {
            review = {} as CifcReview;
        }

        if (!review.id) {
            review.id = uuid.v4();
        }

        review.recent = true;
        review.solved = false;
        review.question_id = question.id;

        return review;
    }

    public onReviewNotificationTypeChange(question: Question, option: string, index: number) {
        let review = this.initializeReview(question);

        review.company_id = question.body.company_id;
        review.notification_type = option;
        review.reason = question.cifc_review?.reason || this.currentReason;
        if (review.notification_type === 'amend') {
            this.commonQuestionnaireService.resetCifcQuestionToOriginalState(question);
            this.reviewPanelOpen[index] = _.cloneDeep(this.reviewPanelOpen[index]);
        }

        if (!this.commonQuestionnaireService.getReviewFromMapByQuestionId(question.id)) {
            this.commonQuestionnaireService.addEntityToReviewMapping(question.id, review);
        }
        if (this.reviewsArray.findIndex((x) => x?.id === question.id) > -1) {
            this.reviewsArray[this.reviewsArray.findIndex((x) => x?.id === question.id)] = review;
        }
        this.commonQuestionnaireService.reviewAddedSubject.next(review);
        this.commonQuestionnaireService.displayOkCifc[index] = true;
    }

    public onReviewReasonChange(event: any, question: Question, index: number) {
        let review = this.initializeReview(question);

        review.company_id = question.body.company_id;
        review.reason = event.target.value;
        this.currentReason = review.reason;
        question.cifc_review.reason = review.reason;
        if (!review.notification_type && question?.cifc_review?.notification_type) {
            review.notification_type = question.cifc_review.notification_type;
        }

        if (!this.commonQuestionnaireService.getReviewFromMapByQuestionId(question.id)) {
            this.commonQuestionnaireService.addEntityToReviewMapping(question.id, review);
        }

        if (this.reviewsArray.findIndex((x) => x?.id === question.id) > -1) {
            this.reviewsArray[this.reviewsArray.findIndex((x) => x?.id === question.id)] = review;
        }

        this.commonQuestionnaireService.reviewAddedSubject.next(review);
        this.commonQuestionnaireService.displayOkCifc[index] = true;
    }

    public scrollToQuestionFn(question: QuestionResponse) {
        if (!question) {
            return;
        }

        let element: Element | null = document.getElementById(question.id ?? '');

        if (!element) {
            return;
        }

        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
}
